import MainLayout from "@/components/layout/admin/MainLayout";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import axios from "axios";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import moment from "moment";

const MAX_MONTH_RANGE = 3;

export default {
  components: {
    MainLayout,
    Loading,
    flatPickr
  },
  data() {
    return {
      isLoading: false,
      searchData: {
        from_date: moment().startOf("month").format("YYYY-MM-DD"),
        to_date: moment().endOf("month").format("YYYY-MM-DD")
      },
      searchError: "",
      reportData: {
        months: []
      }
    };
  },
  computed: {
    /**
     * Flatpickr config: regular day-level date picker (YYYY-MM-DD).
     * The user picks a From Date and a To Date; the backend groups the
     * result by month automatically.
     */
    dateConfig() {
      return {
        dateFormat: "Y-m-d",
        altFormat: "d/m/Y",
        allowInput: false
      };
    },
    /**
     * Pre-computed monthly summary tables.
     * Transposes the API response (payment methods as rows) into the
     * design (summary type as rows, payment method names as columns),
     * so the template does not do any number casts or lookups per render.
     */
    monthlySummaries() {
      const months = (this.reportData && this.reportData.months) || [];
      return months.map((m) => this.buildMonthSummary(m));
    },
    /**
     * True when at least one month of data is available to render.
     */
    hasReportData() {
      return this.monthlySummaries.length > 0;
    }
  },
  methods: {
    /**
     * Cast a value to a finite number, defaulting to 0.
     */
    toNumber(value) {
      const n = parseFloat(value);
      return isNaN(n) ? 0 : n;
    },
    /**
     * Format a number for display. Returns "—" for zero / falsy values to
     * keep the summary table compact.
     */
    formatNumber(value) {
      const n = this.toNumber(value);
      if (!n) return "—";
      return n.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },
    /**
     * Format a signed number. Used for Total OUT so negative values are
     * displayed with a leading minus sign.
     */
    formatSignedNumber(value) {
      const n = this.toNumber(value);
      if (!n) return "—";
      const sign = n < 0 ? "-" : "";
      const absStr = Math.abs(n).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
      return sign + absStr;
    },
    /**
     * Count the inclusive number of months the selected date range
     * covers. Used by the validator to enforce the search range limit
     * (no more than MAX_MONTH_RANGE months per search).
     */
    countMonthsInRange(fromDateStr, toDateStr) {
      const from = moment(fromDateStr, "YYYY-MM-DD", true);
      const to = moment(toDateStr, "YYYY-MM-DD", true);
      if (!from.isValid() || !to.isValid()) return 0;
      return (to.year() - from.year()) * 12 + (to.month() - from.month()) + 1;
    },
    /**
     * Build a per-month summary
     */
    buildMonthSummary(monthData) {
      const raw = monthData.payment_methods || [];
      const paymentMethods = raw.map((pm) => ({
        id: pm.payment_method_id,
        name: pm.payment_method
      }));
      const beginning = raw.map((pm) => this.toNumber(pm.beginning_balance));
      const totalIn = raw.map((pm) => this.toNumber(pm.total_in));
      // Total OUT is displayed as a negative value per the spec.
      const totalOut = raw.map((pm) => -Math.abs(this.toNumber(pm.total_out)));
      const ending = raw.map((pm) => this.toNumber(pm.ending_balance));
      return {
        month: monthData.month,
        monthLabel: this.formatMonthLabel(
          monthData.month,
          monthData.from_date,
          monthData.to_date
        ),
        fromDate: monthData.from_date,
        toDate: monthData.to_date,
        paymentMethods,
        beginning,
        totalIn,
        totalOut,
        ending
      };
    },
    /**
     * Convert "2026-06" to a human-readable label like "June 2026
     * (2026-06-15 ~ 2026-06-30)", appending the effective start/end
     * dates of the month so the section title shows the exact date
     * range covered (including partial first/last months).
     */
    formatMonthLabel(monthStr, fromDate, toDate) {
      const m = moment(monthStr + "-01", "YYYY-MM-DD", true);
      if (!m.isValid()) return monthStr;
      const label = m.format("MMMM YYYY");
      if (fromDate && toDate) {
        return label + " (" + fromDate + " ~ " + toDate + ")";
      }
      return label;
    },
    /**
     * Validate the search form before submitting the API request.
     */
    validateDateRange() {
      const from = this.searchData.from_date;
      const to = this.searchData.to_date;
      if (!from || !to) {
        this.searchError = "Please select both From Date and To Date.";
        return false;
      }
      const fromMoment = moment(from, "YYYY-MM-DD", true);
      const toMoment = moment(to, "YYYY-MM-DD", true);
      if (!fromMoment.isValid() || !toMoment.isValid()) {
        this.searchError = "Please enter valid dates.";
        return false;
      }
      if (fromMoment.isAfter(toMoment)) {
        this.searchError = "From Date cannot be after To Date.";
        return false;
      }
      const months = this.countMonthsInRange(from, to);
      if (months > MAX_MONTH_RANGE) {
        this.searchError =
          "Date range cannot exceed " +
          MAX_MONTH_RANGE +
          " months. Selected range is " +
          months +
          " months.";
        return false;
      }
      this.searchError = "";
      return true;
    },
    reset() {
      this.searchData.from_date = moment().startOf("month").format("YYYY-MM-DD");
      this.searchData.to_date = moment().endOf("month").format("YYYY-MM-DD");
      this.searchError = "";
      this.search();
    },
    search() {
      if (!this.validateDateRange()) return;
      this.isLoading = true;
      this.reportData = { months: [] };
      axios
        .get("/cashflow/monthly", {
          params: {
            from_date: this.searchData.from_date,
            to_date: this.searchData.to_date
          }
        })
        .then((response) => {
          this.isLoading = false;
          if (response.data) {
            this.reportData = response.data;
          }
        })
        .catch((error) => {
          if (error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          this.isLoading = false;
          console.error(error);
        });
    }
  },
  mounted() {
    this.search();
  }
};
